import React, { useId, useMemo } from 'react';
import type {
  BasePatcherProps,
  WithPassedPatchableGroupProps
} from './PatchableGroup';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectProps
} from '@mui/material';
import { Emphasis } from '@/modules/surgicalEvent';
import { XOR } from '@ianesthesie-kit/core';

export type SelectPatcherOption = {
  id: string;
  name: string;
  emphasis?: Emphasis;
};

type FormattedOptions = {
  options: SelectPatcherOption[];
};
type UnformattedOptions<T> = {
  options: T[];
  optionIdKey: keyof T;
  optionLabelKey: keyof T;
  optionEmphasisGetter?: (opt: T) => Emphasis;
};
type OptionProps<T> = XOR<FormattedOptions, UnformattedOptions<T>>;

export type SelectPatcherProps<T> = BasePatcherProps &
  Omit<SelectProps, keyof BasePatcherProps> &
  OptionProps<T>;

const SelectPatcherComponent = <T, Opt extends object>({
  /* eslint-disable @typescript-eslint/no-unused-vars */
  patchable,
  options,
  optionIdKey,
  optionLabelKey,
  optionEmphasisGetter,
  label,
  wrapper,
  name,
  error,
  loading,
  sourceType,
  hideLoadingOverlay,
  /* eslint-enable @typescript-eslint/no-unused-vars */
  ...restProps
}: WithPassedPatchableGroupProps<T, SelectPatcherProps<Opt>>) => {
  const labelId = useId();
  const formattedOptions: SelectPatcherOption[] = useMemo(
    () =>
      options.map((opt) => {
        if ('id' in opt && 'name' in opt) {
          return opt as SelectPatcherOption;
        }
        if (!optionIdKey) {
          throw new Error('TODO');
        }
        if (!optionLabelKey) {
          throw new Error('TODO');
        }
        return {
          id: `${opt[optionIdKey]}`,
          name: `${opt[optionLabelKey]}`,
          emphasis: optionEmphasisGetter?.(opt) || Emphasis.NOT_APPLICABLE
        };
      }),
    [optionEmphasisGetter, optionIdKey, optionLabelKey, options]
  );

  if (!patchable) {
    throw new Error(
      'A Patcher component must be used as a child of a PatchableGroup'
    );
  }

  const doPatch = (v: string) => {
    if (patchable) {
      const { onPatch } = patchable;
      onPatch({ [name]: v });
    }
  };

  return (
    <FormControl
      disabled={loading}
      error={!!error}
      fullWidth
      size="small"
      color="riskNone"
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        readOnly={patchable.readOnly}
        value={patchable.currentValue || ''}
        labelId={labelId}
        label={label}
        onChange={(e) => doPatch(e.target.value as string)}
        {...restProps}
      >
        {formattedOptions.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            {opt.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const SelectPatcher = <Opt extends object>(
  props: SelectPatcherProps<Opt>
) => <SelectPatcherComponent {...props} />;
